/**
 * Coupon Manager - Gutschein-Verwaltung
 * Erstellen, verwalten, und tracken von Rabatt-Codes
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Percent, Plus, Trash2, Copy, CheckCircle, TrendingUp, Gift, Calendar,
  Euro, Users, Clock, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function CouponManager() {
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Form state
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    name: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_order_value: null,
    max_uses: null,
    max_uses_per_customer: 1,
    valid_until: '',
    first_order_only: false,
    active: true
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [couponsRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/api/coupons`, { headers }),
        fetch(`${API_BASE}/api/coupons/stats`, { headers })
      ]);

      if (couponsRes.ok) {
        const data = await couponsRes.json();
        setCoupons(data.coupons);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const createCoupon = async () => {
    if (!newCoupon.code || !newCoupon.name) {
      toast.error('Code und Name erforderlich');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/coupons`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCoupon)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Gutschein "${data.code}" erstellt!`);
        setShowCreateDialog(false);
        setNewCoupon({
          code: '',
          name: '',
          discount_type: 'percentage',
          discount_value: 10,
          min_order_value: null,
          max_uses: null,
          max_uses_per_customer: 1,
          valid_until: '',
          first_order_only: false,
          active: true
        });
        loadAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  const deleteCoupon = async (couponId, code) => {
    if (!confirm(`Gutschein "${code}" wirklich löschen?`)) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/coupons/${couponId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Gelöscht');
        loadAllData();
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code kopiert!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#0B1220] to-[#0E1628]">
        <div className="text-white">Lädt Gutscheine...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1220] to-[#0E1628] p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Gutschein-Verwaltung
          </h1>
          <p className="text-slate-400">
            Rabattcodes erstellen und tracken
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="bg-[#111C32]/80 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{stats.active_coupons || 0}</div>
                  <div className="text-xs text-slate-400">Aktive Codes</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111C32]/80 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{stats.redeemed_today || 0}</div>
                  <div className="text-xs text-slate-400">Eingelöst heute</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111C32]/80 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Euro className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{stats.total_discount_today?.toFixed(2) || '0.00'}€</div>
                  <div className="text-xs text-slate-400">Rabatt heute</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111C32]/80 border-slate-700/50">
            <CardContent className="p-4">
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="w-full h-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white"
                data-testid="create-coupon-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Neuer Gutschein
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Coupons Liste */}
        <div className="grid grid-cols-1 gap-4">
          {coupons.map((coupon) => {
            const isExpired = coupon.valid_until && new Date(coupon.valid_until) < new Date();
            const usagePercent = coupon.max_uses 
              ? Math.round((coupon.usage_count / coupon.max_uses) * 100)
              : 0;

            return (
              <Card key={coupon.coupon_id} className="bg-[#111C32] border-slate-700/50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-teal-500/20 to-emerald-500/20 flex items-center justify-center border border-teal-500/30">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-teal-400">
                            {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `${coupon.discount_value}€`}
                          </div>
                          <div className="text-[8px] text-teal-400/70">OFF</div>
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <CardTitle className="text-white">{coupon.name}</CardTitle>
                          {isExpired && (
                            <Badge variant="outline" className="text-red-400 border-red-400/50">
                              Abgelaufen
                            </Badge>
                          )}
                          {!coupon.active && (
                            <Badge variant="outline" className="text-slate-400 border-slate-400/50">
                              Inaktiv
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-slate-400">
                          <button
                            onClick={() => copyCode(coupon.code)}
                            className="flex items-center gap-1 hover:text-teal-400 transition-colors"
                            data-testid={`copy-code-${coupon.coupon_id}`}
                          >
                            <Copy className="w-3 h-3" />
                            <code className="font-mono font-bold">{coupon.code}</code>
                          </button>
                          
                          {coupon.min_order_value && (
                            <span className="flex items-center gap-1">
                              <Euro className="w-3 h-3" />
                              Ab {coupon.min_order_value}€
                            </span>
                          )}
                          
                          {coupon.valid_until && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Bis {new Date(coupon.valid_until).toLocaleDateString('de-DE')}
                            </span>
                          )}
                          
                          {coupon.first_order_only && (
                            <Badge variant="outline" className="text-blue-400 border-blue-400/50 text-xs">
                              Nur Neukunden
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="text-2xl font-bold text-white">
                          {coupon.usage_count || 0}
                        </div>
                        <div className="text-xs text-slate-400">
                          {coupon.max_uses ? `von ${coupon.max_uses}` : 'Einlösungen'}
                        </div>
                        {coupon.max_uses && (
                          <div className="w-20 h-1 bg-slate-700 rounded-full mt-1 overflow-hidden">
                            <div
                              className="h-full bg-teal-500 transition-all"
                              style={{ width: `${usagePercent}%` }}
                            />
                          </div>
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteCoupon(coupon.coupon_id, coupon.code)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`delete-coupon-${coupon.coupon_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            );
          })}

          {coupons.length === 0 && (
            <Card className="bg-[#111C32]/50 border-slate-700/30">
              <CardContent className="p-12 text-center">
                <Gift className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                <p className="text-slate-400 mb-2">Noch keine Gutscheine erstellt</p>
                <p className="text-slate-500 text-sm mb-4">
                  Erstellen Sie Rabattcodes für Kampagnen und Aktionen
                </p>
                <Button
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-teal-500 hover:bg-teal-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ersten Gutschein erstellen
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Create Coupon Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="bg-[#111C32]/95 backdrop-blur-xl border-slate-700/60 max-w-2xl">
            <div className="h-1 absolute top-0 left-0 right-0 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-t-lg" />
            
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 text-xl text-white">
                <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
                  <Gift className="text-teal-400" size={20} />
                </div>
                Neuen Gutschein erstellen
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Gutschein-Code *</Label>
                  <Input
                    value={newCoupon.code}
                    onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
                    className="mt-1 bg-[#0E1628] border-slate-700 text-white font-mono"
                    placeholder="SOMMER2024"
                    data-testid="coupon-code-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Nur Großbuchstaben und Zahlen</p>
                </div>

                <div>
                  <Label className="text-slate-300">Name *</Label>
                  <Input
                    value={newCoupon.name}
                    onChange={(e) => setNewCoupon({...newCoupon, name: e.target.value})}
                    className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                    placeholder="Sommer-Aktion 2024"
                    data-testid="coupon-name-input"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Rabatt-Typ</Label>
                  <div className="flex gap-2 mt-1">
                    {['percentage', 'fixed_amount', 'free_delivery'].map((type) => (
                      <button
                        key={type}
                        onClick={() => setNewCoupon({...newCoupon, discount_type: type})}
                        className={`
                          flex-1 p-2 rounded-lg border-2 text-sm font-semibold transition-all
                          ${newCoupon.discount_type === type
                            ? 'border-teal-500 bg-teal-500/15 text-white'
                            : 'border-slate-700 bg-slate-800/30 text-slate-400'
                          }
                        `}
                        data-testid={`discount-type-${type}`}
                      >
                        {type === 'percentage' && '%'}
                        {type === 'fixed_amount' && '€'}
                        {type === 'free_delivery' && '🚚'}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300">Rabatt-Wert</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={newCoupon.discount_value}
                    onChange={(e) => setNewCoupon({...newCoupon, discount_value: parseFloat(e.target.value)})}
                    className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                    data-testid="discount-value-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {newCoupon.discount_type === 'percentage' ? 'Prozent' : 'Euro'}
                  </p>
                </div>

                <div>
                  <Label className="text-slate-300">Mindestbestellwert (€)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={newCoupon.min_order_value || ''}
                    onChange={(e) => setNewCoupon({...newCoupon, min_order_value: e.target.value ? parseFloat(e.target.value) : null})}
                    className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                    placeholder="Optional"
                    data-testid="min-order-value-input"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Max. Einlösungen (Gesamt)</Label>
                  <Input
                    type="number"
                    value={newCoupon.max_uses || ''}
                    onChange={(e) => setNewCoupon({...newCoupon, max_uses: e.target.value ? parseInt(e.target.value) : null})}
                    className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                    placeholder="Unbegrenzt"
                    data-testid="max-uses-input"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Max. pro Kunde</Label>
                  <Input
                    type="number"
                    value={newCoupon.max_uses_per_customer}
                    onChange={(e) => setNewCoupon({...newCoupon, max_uses_per_customer: parseInt(e.target.value)})}
                    className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                    data-testid="max-per-customer-input"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Gültig bis</Label>
                  <Input
                    type="datetime-local"
                    value={newCoupon.valid_until}
                    onChange={(e) => setNewCoupon({...newCoupon, valid_until: e.target.value})}
                    className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                    data-testid="valid-until-input"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg">
                <Label className="text-slate-300">Nur für Neukunden</Label>
                <Switch
                  checked={newCoupon.first_order_only}
                  onCheckedChange={(checked) => setNewCoupon({...newCoupon, first_order_only: checked})}
                  className="data-[state=checked]:bg-teal-500"
                  data-testid="first-order-only-switch"
                />
              </div>
            </div>

            <DialogFooter className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                className="flex-1 h-12 border-slate-600 text-white hover:bg-slate-700"
              >
                Abbrechen
              </Button>
              <Button
                onClick={createCoupon}
                className="flex-1 h-12 bg-gradient-to-r from-teal-600 to-emerald-500"
                data-testid="save-coupon-btn"
              >
                <Gift className="w-4 h-4 mr-2" />
                Gutschein erstellen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
}
